import { MoreOutlined, WarningFilled } from '@ant-design/icons';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useRequest } from 'ahooks';
import { Dropdown, Input, List, Modal } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import dayjs from 'dayjs';
import posthog from 'posthog-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import ApiPatientDocumentsManager, { PatientDocument } from 'services/api/ApiPatientDocumentsManager';
import { Button } from 'components/ui';
import { colors } from 'design';

const useStyles = createUseStyles({
  documentItem: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  documentInfo: {
    flex: 1,
    marginRight: 16,
    minWidth: 0, // Allow flex item to shrink below content size
    overflow: 'hidden',
  },
  documentName: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 4,
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  documentMeta: {
    fontSize: 12,
    color: colors.text.tertiary,
  },
  documentActions: {
    display: 'flex',
    gap: 8,
  },
});

export type PatientDocumentsListProps = {
  documents: PatientDocument[];
  isCaregiver?: boolean;
  currentCaregiver?: { id: string; display_name: string } | null;
  onRefresh?: () => void;
};

export default function PatientDocumentsList({
  documents,
  isCaregiver = false,
  currentCaregiver,
  onRefresh,
}: PatientDocumentsListProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [api, context] = useNotification();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<PatientDocument | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');

  const isSharedWithCurrentCaregiver = (document: PatientDocument): boolean => {
    if (!currentCaregiver) return false;
    return document.shared_with_caregivers.includes(currentCaregiver.id);
  };

  // Fetch and open document
  const { loading: fetchingDocument, run: fetchAndOpenDocument } = useRequest(
    (documentId: string) =>
      isCaregiver
        ? ApiPatientDocumentsManager.retrieveForCaregiver(documentId)
        : ApiPatientDocumentsManager.retrieve(documentId),
    {
      manual: true,
      onSuccess: async (document: PatientDocument) => {
        if (!document.file_url) {
          api.error({ message: t('documents.errors.loadError') });
          return;
        }
        if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
          await Browser.open({ url: document.file_url });
        } else {
          window.open(document.file_url, '_blank');
        }
      },
      onError: () => {
        api.error({ message: t('documents.errors.loadError') });
      },
    },
  );

  // Share document
  const { loading: sharing, run: shareDocument } = useRequest(
    ({ documentId, caregiverId }: { documentId: string; caregiverId: string }) =>
      ApiPatientDocumentsManager.share({ documentId, caregiverId }),
    {
      manual: true,
      onSuccess: () => {
        api.success({ message: t('documents.shareSuccess') });
        onRefresh?.();
      },
      onError: () => {
        api.error({ message: t('documents.errors.shareError') });
      },
    },
  );

  // Unshare document
  const { loading: unsharing, run: unshareDocument } = useRequest(
    ({ documentId, caregiverId }: { documentId: string; caregiverId: string }) =>
      ApiPatientDocumentsManager.unshare({ documentId, caregiverId }),
    {
      manual: true,
      onSuccess: () => {
        api.success({ message: t('documents.unshareSuccess') });
        onRefresh?.();
      },
      onError: () => {
        api.error({ message: t('documents.errors.unshareError') });
      },
    },
  );

  // Delete document
  const { loading: deleting, run: deleteDocument } = useRequest(
    (documentId: string) => ApiPatientDocumentsManager.delete(documentId),
    {
      manual: true,
      onSuccess: () => {
        api.success({ message: t('documents.deleteSuccess') });
        onRefresh?.();
      },
      onError: () => {
        api.error({ message: t('documents.errors.deleteError') });
      },
    },
  );

  // Update document
  const { loading: updating, run: updateDocument } = useRequest(
    ({ documentId, filename }: { documentId: string; filename: string }) =>
      ApiPatientDocumentsManager.update({ documentId, filename }),
    {
      manual: true,
      onSuccess: () => {
        api.success({ message: t('documents.updateSuccess') });
        setEditModalOpen(false);
        setEditingDocument(null);
        setEditDisplayName('');
        onRefresh?.();
      },
      onError: () => {
        api.error({ message: t('documents.errors.updateError') });
      },
    },
  );

  const loading = sharing || unsharing || deleting || updating || fetchingDocument;

  // Handler functions
  const handleView = (document: PatientDocument) => {
    fetchAndOpenDocument(document.id);
    posthog.capture('patient_documents.document_viewed');
  };

  const handleEdit = (document: PatientDocument) => {
    setEditingDocument(document);
    setEditDisplayName(document.filename);
    setEditModalOpen(true);
  };

  const handleEditSave = () => {
    if (!editingDocument) return;
    const trimmedName = editDisplayName.trim();
    if (!trimmedName) {
      api.error({
        message: t('documents.errors.emptyName'),
      });
      return;
    }
    updateDocument({ documentId: editingDocument.id, filename: trimmedName });
    posthog.capture('patient_documents.document_renamed');
  };

  const handleShare = (document: PatientDocument) => {
    if (!currentCaregiver) {
      api.warning({
        message: t('documents.errors.noCaregiver'),
      });
      return;
    }
    shareDocument({ documentId: document.id, caregiverId: currentCaregiver.id });
    posthog.capture('patient_documents.document_shared');
  };

  const handleUnshare = (document: PatientDocument) => {
    if (!currentCaregiver) {
      return;
    }
    unshareDocument({ documentId: document.id, caregiverId: currentCaregiver.id });
    posthog.capture('patient_documents.document_unshared');
  };

  const handleDelete = (document: PatientDocument) => {
    Modal.confirm({
      content: t('documents.confirmDelete', { fileName: document.filename }),
      okText: t('common.delete'),
      okButtonProps: { danger: true, style: { backgroundColor: colors.dataViz.veryBad } },
      cancelText: t('common.cancel'),
      icon: <WarningFilled style={{ color: colors.dataViz.bad }} />,

      onOk: () => deleteDocument(document.id),
    });
    posthog.capture('patient_documents.document_deleted');
  };

  const renderDocumentItem = (document: PatientDocument) => {
    const isShared = isSharedWithCurrentCaregiver(document);
    const menuItems = [
      {
        key: 'view',
        label: t('documents.view'),
        onClick: () => handleView(document),
      },
      // Only show edit if not caregiver
      ...(!isCaregiver
        ? [
            {
              key: 'edit',
              label: t('documents.edit'),
              onClick: () => handleEdit(document),
            },
          ]
        : []),
      // Share/unshare options (only for patients, not caregivers)
      ...(!isCaregiver && currentCaregiver
        ? !isShared
          ? [
              {
                key: 'share',
                label: t('documents.shareWith', {
                  caregiverName: currentCaregiver.display_name,
                }),
                onClick: () => handleShare(document),
              },
            ]
          : [
              {
                key: 'unshare',
                label: t('documents.unshareWith', {
                  caregiverName: currentCaregiver.display_name,
                }),
                onClick: () => handleUnshare(document),
              },
            ]
        : []),
      // Only show delete if not caregiver
      ...(!isCaregiver
        ? [
            {
              key: 'delete',
              label: t('common.delete'),
              danger: true,
              onClick: () => handleDelete(document),
            },
          ]
        : []),
    ];

    const metaText = `${dayjs(document.uploaded_at).format('DD/MM/YYYY HH:mm')}`;

    return (
      <div className={styles.documentItem}>
        <div className={styles.documentInfo}>
          <div className={styles.documentName} onClick={() => handleView(document)}>
            {document.filename}
          </div>
          <div className={styles.documentMeta}>{metaText}</div>
        </div>
        {menuItems.length > 1 && (
          <div className={styles.documentActions}>
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button variant="ghost" icon={<MoreOutlined />} loading={loading} />
            </Dropdown>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {context}
      <List dataSource={documents} renderItem={renderDocumentItem} />
      <Modal
        title={t('documents.editName')}
        open={editModalOpen}
        onOk={handleEditSave}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingDocument(null);
          setEditDisplayName('');
        }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={updating}
      >
        <Input
          value={editDisplayName}
          onChange={e => setEditDisplayName(e.target.value)}
          placeholder={t('documents.enterName')}
          maxLength={255}
          onPressEnter={handleEditSave}
        />
      </Modal>
    </>
  );
}
